import React, {FC, useState, ReactNode} from "react"
import CopyToClipboard from "react-copy-to-clipboard"

import Button from "@irati/ui/src/Button/Button"
import Loading from "@irati/ui/src/Loading"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Card from "@irati/ui/src/Layout/Card"
import {Body} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {Deal} from "@irati/types"

import {adminDashboardTracking} from "@irati-tracking/admin"
import {WidgetFilterType} from "@irati-tracking/admin/adminDashboard"
import Region, {ALL_TEAMS} from "../../../../Reporting/Region"
import TopPerk from "../TopPerk"
import {getBoldCompaniesText} from "./phraseKeys"

interface ReportingDeal extends Deal {
  savingsAUD?: number
  savingsGBP?: number
  uniqueUsers?: number
}

interface Props {
  onUpdateRegion: () => void
  onBack: () => void
  perks: ReportingDeal[]
  load: Promise<any> | null
  regionId: string
  topPerksText: ReactNode
  copyText: string
  bodyText: ReactNode
  shareText: string
}

const TopPerks: FC<Props> = ({onUpdateRegion, onBack, perks, load, regionId, topPerksText, copyText, bodyText, shareText}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const copyButtonText = isCopied
    ? "admin.components.dashboard.reporting.costOfLiving.modal.copiedToClipboard"
    : "admin.components.dashboard.reporting.costOfLiving.modal.copyButton"
  const bold = getBoldCompaniesText()

  const handleUpdateRegion = (updatedRegionId: string, updatedRegion: {label: string}) => {
    adminDashboardTracking.costOfLivingActions("perksfilterCountry", updatedRegionId !== ALL_TEAMS, [WidgetFilterType.AdminWidgetCountry])
    setIsCopied(false)
    onUpdateRegion(updatedRegionId, updatedRegion)
  }

  const handleBack = () => {
    setIsCopied(false)
    onBack()
  }

  const handleCopy = () => {
    const isFiltered = regionId !== ALL_TEAMS
    const filter = isFiltered ? [WidgetFilterType.AdminWidgetCountry] : undefined
    adminDashboardTracking.costOfLivingActions("cta_perkscopyText", isFiltered, filter)
    setIsCopied(true)
  }

  return (
    <>
      {isCopied && (
        <Alert
          type={AlertTypes.success}
          title="admin.components.dashboard.reporting.costOfLiving.perksModal.alert"
          onHide={() => setIsCopied(false)}
        />
      )}
      <Body maxWidth="100%" marginBottom={1}>
        <FormattedMessage id={shareText} values={{bold}} />
      </Body>
      <Flex justifyContent="flex-end">
        <Region id="adminDashboard_reporting_country" value={regionId} onChange={handleUpdateRegion} />
      </Flex>
      <Card marginTop={2} marginBottom={2}>
        <Body maxWidth="100%" textAlign="center" weight="bold">
          <FormattedMessage id="admin.components.dashboard.reporting.costOfLiving.modal.flexiPerks.body2" />
        </Body>
        <Body maxWidth="100%" textAlign="center" marginBottom={3}>
          {bodyText}
        </Body>
        <Body maxWidth="100%" textAlign="center" weight="bold" marginBottom={2}>
          {topPerksText}
        </Body>
        <Loading load={load}>
          <Flex justifyContent="center" gap={3}>
            {perks.map((perk, index) => (
              <TopPerk perk={perk} ranking={index + 1} />
            ))}
          </Flex>
          {perks.length === 0 && (
            <Body maxWidth="100%" textAlign="center">
              <FormattedMessage id="admin.components.dashboard.reporting.costOfLiving.modal.empty" />
            </Body>
          )}
        </Loading>
      </Card>
      <Flex justifyContent="space-between" alignItems="center">
        <Button tertiary onClick={handleBack}>
          <Icon icon={IconTypes.chevronLeft} marginRight />
          <FormattedMessage id="platform.button.back" />
        </Button>
        {perks.length > 0 && (
          <CopyToClipboard text={copyText}>
            <Button onClick={handleCopy}>
              <FormattedMessage id={copyButtonText} />
            </Button>
          </CopyToClipboard>
        )}
      </Flex>
    </>
  )
}

export default TopPerks
